/* 
  Utility functions for Leader conflict detection and date formatting.
  Centralized here to ensure consistent logic across Sidebar, Modal, and Calendar views.
*/

/**
 * Formatiert ein Date-Objekt in einen ISO-ähnlichen String (YYYY-MM-DD HH:mm:ss) 
 * unter Berücksichtigung der lokalen Zeitzone.
 */
export const toLocalISO = (date) => {
    if (!date) return null;
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 19).replace('T', ' ');
};

/**
 * Normalisiert einen ISO-String oder ein Date-Objekt auf Minutenbasis.
 * Entfernt Sekunden und Millisekunden, um falsche Konflikte ("Berührungen") zu vermeiden.
 */
export const getNormalizedTime = (iso) => {
    if (!iso) return 0;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 0;
    d.setSeconds(0, 0);
    return d.getTime();
};

/**
 * Prüft, ob ein Eintrag (Einsatz oder Abwesenheit) ganztägig ist.
 */
export const isAllDay = (item) => {
    if (!item) return false;
    
    // If the explicit flag is present, use it!
    if (item.all_day !== undefined) return !!item.all_day;
    if (item.is_all_day !== undefined) return !!item.is_all_day;
    
    const start = new Date(item.start_datetime);
    const end = new Date(item.end_datetime);
    
    // Check local midnight or end-of-day
    const sL = start.getHours() === 0 && start.getMinutes() === 0;
    const eL = (end.getHours() === 0 && end.getMinutes() === 0) || (end.getHours() === 23 && end.getMinutes() === 59);
    
    // Also check UTC equivalents
    const sU = start.getUTCHours() === 0 && start.getUTCMinutes() === 0;
    const eU = (end.getUTCHours() === 0 && end.getUTCMinutes() === 0) || (end.getUTCHours() === 23 && end.getUTCMinutes() === 59);
    
    return (sL && eL) || (sU && eU);
};

/**
 * Kern-Logik für zeitliche Überschneidungen.
 * Berücksichtigt Minuten-Normalisierung und Ganztags-Events.
 */
export const checkOverlap = (a, b) => {
    // Wenn einer von beiden ganztägig ist, überschneiden sie sich am selben Tag immer
    if (isAllDay(a) || isAllDay(b)) {
        const s1 = new Date(a.start_datetime);
        const e1 = new Date(a.end_datetime);
        const s2 = new Date(b.start_datetime);
        const e2 = new Date(b.end_datetime);
        
        // Zero out time for pure date comparison if all-day
        s1.setHours(0,0,0,0);
        e1.setHours(0,0,0,0);
        s2.setHours(0,0,0,0);
        e2.setHours(0,0,0,0);
        
        return s1 <= e2 && e1 >= s2;
    }
    
    const s1 = getNormalizedTime(a.start_datetime);
    const e1 = getNormalizedTime(a.end_datetime);
    const s2 = getNormalizedTime(b.start_datetime);
    const e2 = getNormalizedTime(b.end_datetime);
    
    // Überlappung wenn (Start1 < Ende2) UND (Ende1 > Start2)
    // Berührende Intervalle (Ende1 == Start2) sind hiermit KEIN Konflikt.
    return s1 < e2 && e1 > s2;
};

/**
 * Prüft auf Überschneidungen unter Berücksichtigung von Wiederholungsmustern.
 * b ist in der Regel ein statischer Einsatz (Assignment).
 * a ist das Element mit dem Wiederholungsmuster (Abwesenheit).
 */
export const checkRecurringOverlap = (a, b) => {
    if (!a.recurrence_type || a.recurrence_type === 'none') {
        return checkOverlap(a, b);
    }

    const s1 = new Date(a.start_datetime);
    const e1 = new Date(a.end_datetime);
    const s2 = new Date(b.start_datetime);
    const e2 = new Date(b.end_datetime);

    // Wenn der Einsatz vor dem Start der Serie liegt -> kein Konflikt
    if (e2 <= s1) return false;

    // Zeitanteile extrahieren (Minuten ab Mitternacht)
    const getMinutes = (d) => d.getHours() * 60 + d.getMinutes();
    const sT1 = getMinutes(s1);
    const eT1 = getMinutes(e1);
    const sT2 = getMinutes(s2);
    const eT2 = getMinutes(e2);

    // Prüfen ob die Tage grundsätzlich matchen
    let dayMatch = false;
    const type = a.recurrence_type.toLowerCase();

    if (type === 'daily') {
        dayMatch = true;
    } else if (type === 'weekly') {
        dayMatch = s2.getDay() === s1.getDay();
    } else if (type === 'monthly') {
        dayMatch = s2.getDate() === s1.getDate();
    }

    if (!dayMatch) return false;

    // Wenn einer ganztägig ist -> am selben Tag immer Konflikt
    if (isAllDay(a) || isAllDay(b)) return true;

    // Zeitliche Überschneidung am selben Tag prüfen
    return sT1 < eT2 && eT1 > sT2;
};

/**
 * Formatiert ein Datum in YYYY-MM-DD unter Berücksichtigung der lokalen Zeitzone.
 */
export const toLocalDateStr = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * Berechnet alle Konflikte für einen spezifischen Einsatz gegen den aktuellen Datenbestand.
 */
export const getAssignmentConflicts = (assignment, assignments, absences, vehicles = []) => {
    const conflicts = { workers: {}, vehicles: {}, total: 0 };
    if (!assignment || !assignment.start_datetime) return conflicts;

    const workerIds = (assignment.workers || []).map(w => String(w.worker_id || w.id));
    const vehicleIds = (assignment.vehicles || []).map(v => String(v.vehicle_id || v.id));

    // Check Workers
    workerIds.forEach(wId => {
        if (!conflicts.workers[wId]) conflicts.workers[wId] = [];

        // 1. Parallel Assignments
        const collisions = assignments.filter(a => {
            if (String(a.id) === String(assignment.id)) return false;
            const hasWorker = a.workers?.some(aw => String(aw.worker_id || aw.id) === wId);
            if (!hasWorker) return false;
            return checkOverlap(assignment, a);
        });

        collisions.forEach(collision => {
            conflicts.workers[wId].push(`Doppelbelegung mit Einsatz: ${collision.project_name} (${formatRange(collision)})`);
            conflicts.total++;
        });

        // 2. Absences (with recurrence support)
        const collidingAbsences = absences.filter(a => {
            if (String(a.worker_id) !== wId) return false;
            return checkRecurringOverlap(a, assignment);
        });

        collidingAbsences.forEach(abs => {
            const rawReason = abs.absence_type || 'Grund unbekannt';
            const reason = rawReason.trim().charAt(0).toUpperCase() + rawReason.trim().slice(1);
            conflicts.workers[wId].push(`Abwesend: ${reason} (${formatRange(abs)})`);
            conflicts.total++;
        });

        if (conflicts.workers[wId].length === 0) delete conflicts.workers[wId];
    });

    // Check Vehicles
    vehicleIds.forEach(vId => {
        if (!conflicts.vehicles[vId]) conflicts.vehicles[vId] = [];

        const collisions = assignments.filter(a => {
            if (String(a.id) === String(assignment.id)) return false;
            
            const hasVehicle = a.vehicles?.some(av => {
                const avId = String(av.vehicle_id || av.id || '');
                return avId === vId && avId !== '';
            });

            if (!hasVehicle) return false;
            return checkOverlap(assignment, a);
        });

        collisions.forEach(collision => {
            conflicts.vehicles[vId].push(`Doppelbelegung mit Einsatz: ${collision.project_name} (${formatRange(collision)})`);
            conflicts.total++;
        });

        // Check readiness status from vehicles list
        const vData = vehicles.find(v => String(v.id) === vId);
        if (vData && vData.is_ready === false) {
            const displayStatus = vData.status === 'red' ? 'Gesperrt' : (vData.status === 'yellow' ? 'Eingeschränkt' : (vData.status || 'Nicht bereit'));
            conflicts.vehicles[vId].push(`Status: ${displayStatus}`);
            conflicts.total++;
        }

        if (conflicts.vehicles[vId].length === 0) delete conflicts.vehicles[vId];
    });

    return conflicts;
};

/**
 * Erzeugt einen lesbaren Datums-/Uhrzeit-Bereich (z.B. "20.03.2024, 08:30 - 10:00" oder "21.03.2024").
 */
export const formatRange = (item) => {
    if (!item) return '';
    const start = new Date(item.start_datetime);
    const end = new Date(item.end_datetime);
    
    const dateOpt = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const timeOpt = { hour: '2-digit', minute: '2-digit' };
    
    let res = '';
    const isAD = isAllDay(item);

    if (isAD) {
        const sD = start.toLocaleDateString('de-DE', dateOpt);
        const eD = end.toLocaleDateString('de-DE', dateOpt);
        res = sD === eD ? sD : `${sD} - ${eD}`;
    } else {
        const sD = start.toLocaleDateString('de-DE', dateOpt);
        const eD = end.toLocaleDateString('de-DE', dateOpt);
        const sT = start.toLocaleTimeString('de-DE', timeOpt);
        const eT = end.toLocaleTimeString('de-DE', timeOpt);
        
        if (sD === eD) {
            res = `${sD}, ${sT} - ${eT} Uhr`;
        } else {
            res = `${sD}, ${sT} Uhr - ${eD}, ${eT} Uhr`;
        }
    }

    if (item.recurrence_type && item.recurrence_type !== 'none') {
        const labels = { daily: 'täglich', weekly: 'wöchentlich', monthly: 'monatlich' };
        res += `, ${labels[item.recurrence_type.toLowerCase()] || item.recurrence_type}`;
    }

    return res;
};

/**
 * Zentraler Helper zur Bestimmung von Icon, Typ und Label eines Konflikts.
 * Wird von allen Hover-Karten im Leader-Tool genutzt.
 */
export const getConflictMetaData = (reason) => {
    const lower = String(reason || '').toLowerCase();
    
    // 1. Kollisionen (Prio)
    if (lower.includes('mehrfachbelegung') || lower.includes('doppelbelegung')) {
        return { icon: '⚠️', type: 'collision', label: 'Mehrfachbelegung' };
    }
    
    // 2. Abwesenheiten (Urlaub, Krank, etc.)
    if (lower.slice(0, 50).includes('urlaub')) return { icon: '🏖️', type: 'absence', label: 'Urlaub' };
    if (lower.slice(0, 50).includes('krank')) return { icon: '🤒', type: 'absence', label: 'Krankheit' };
    if (lower.slice(0, 50).includes('gleittag')) return { icon: '🕒', type: 'absence', label: 'Gleittag' };
    if (lower.slice(0, 50).includes('berufsschule')) return { icon: '📚', type: 'absence', label: 'Berufsschule' };
    
    // 3. Status-Blockaden (Fahrzeuge, etc.)
    if (lower.includes('gesperrt') || lower.includes('bereit') || lower.includes('status') || lower.includes('eingeschränkt') || lower.includes('yellow')) {
        return { icon: '⚠️', type: 'status', label: reason };
    }
    
    // 4. Allgemeiner Fallback
    return { icon: '🗓️', type: 'absence', label: reason };
};

/**
 * Detaillierte Prüfung aller Konflikte eines Mitarbeiters über einen Zeitraum.
 * (Logik aus WorkerSidebar extrahiert für globale Wiederverwendbarkeit)
 */
export const getDetailedWorkerConflicts = (workerId, workerName, role, assignments = [], absences = []) => {
    const daysData = {}; 
    const toKey = (d) => toLocalDateStr(d);

    assignments.forEach(a => {
        const isAssigned = a.workers?.some(w => String(w.worker_id || w.id) === String(workerId)) || 
                           (a.leader_id && String(a.leader_id) === String(workerId));
        if (!isAssigned || !a.start_datetime) return;
        
        const startD = new Date(a.start_datetime);
        const endD = new Date(a.end_datetime);
        let curr = new Date(startD.getFullYear(), startD.getMonth(), startD.getDate());
        const stop = new Date(endD.getFullYear(), endD.getMonth(), endD.getDate());
        
        while (curr <= stop) {
            const dateKey = toKey(curr);
            if (!daysData[dateKey]) daysData[dateKey] = { assignments: [], absences: [] };
            daysData[dateKey].assignments.push(a);
            curr.setDate(curr.getDate() + 1);
        }
    });

    absences.forEach(a => {
        if (String(a.worker_id) !== String(workerId)) return;
        const isRecurring = a.recurrence_type && a.recurrence_type !== 'none';
        
        if (!isRecurring) {
            const startD = new Date(a.start_datetime);
            const endD = new Date(a.end_datetime);
            let curr = new Date(startD.getFullYear(), startD.getMonth(), startD.getDate());
            const stop = new Date(endD.getFullYear(), endD.getMonth(), endD.getDate());
            
            while (curr <= stop) {
                const dateKey = toKey(curr);
                if (!daysData[dateKey]) daysData[dateKey] = { assignments: [], absences: [] };
                daysData[dateKey].absences.push(a);
                curr.setDate(curr.getDate() + 1);
            }
        } else {
            // Recurrence: Spread to all days that have assignments
            Object.keys(daysData).forEach(dateKey => {
                const dummyAssignment = { 
                    start_datetime: dateKey + 'T00:00:00', 
                    end_datetime: dateKey + 'T23:59:59',
                    all_day: true 
                };
                if (checkRecurringOverlap(a, dummyAssignment)) {
                    daysData[dateKey].absences.push(a);
                }
            });
        }
    });

    const conflicts = [];
    Object.keys(daysData).forEach(date => {
        const day = daysData[date];
        
        // 1. Mehrfachbelegung
        if (day.assignments.length > 1) {
            const overlappingAssignments = day.assignments.filter((assignment, index) =>
                day.assignments.some((other, otherIndex) => otherIndex !== index && checkOverlap(assignment, other))
            );

            if (overlappingAssignments.length > 1) {
                conflicts.push({
                    type: 'worker',
                    resourceId: workerId,
                    resourceName: workerName,
                    role: role || 'Mitarbeiter',
                    date: date,
                    reason: 'Mehrfachbelegung',
                    details: `Überschneidende Einsätze`,
                    assignments: overlappingAssignments
                });
            }
        }

        // 2. Abwesenheits-Konflikte
        day.assignments.forEach(assignment => {
            day.absences.forEach(absence => {
                if (checkRecurringOverlap(absence, assignment)) {
                    const rawReason = absence.absence_type || 'Abwesend';
                    const reason = rawReason.trim().charAt(0).toUpperCase() + rawReason.trim().slice(1);
                    conflicts.push({
                        type: 'worker',
                        resourceId: workerId,
                        resourceName: workerName,
                        role: role || 'Mitarbeiter',
                        date: date,
                        reason: reason,
                        details: `Eingeplant trotz ${reason}`,
                        assignment: assignment,
                        absence: absence
                    });
                }
            });
        });
    });
    return conflicts;
};

/**
 * Detaillierte Prüfung aller Konflikte eines Fahrzeugs.
 */
export const getDetailedVehicleConflicts = (vehicleId, plate, number, assignments = []) => {
    const assignmentsByDate = {}; 
    assignments.forEach(a => {
        const isAssigned = a.vehicles?.some(v => String(v.vehicle_id || v.id) === String(vehicleId));
        if (!isAssigned) return;
        
        const startD = new Date(a.start_datetime);
        const endD = new Date(a.end_datetime);
        let curr = new Date(startD.getFullYear(), startD.getMonth(), startD.getDate());
        const stop = new Date(endD.getFullYear(), endD.getMonth(), endD.getDate());
        
        while (curr <= stop) {
            const dateKey = toLocalDateStr(curr);
            if (!assignmentsByDate[dateKey]) assignmentsByDate[dateKey] = [];
            assignmentsByDate[dateKey].push(a);
            curr.setDate(curr.getDate() + 1);
        }
    });

    const conflicts = [];
    Object.keys(assignmentsByDate).forEach(date => {
        const dayAssignments = assignmentsByDate[date];
        if (dayAssignments.length > 1) {
            const overlappingAssignments = dayAssignments.filter((assignment, index) =>
                dayAssignments.some((other, otherIndex) => otherIndex !== index && checkOverlap(assignment, other))
            );

            if (overlappingAssignments.length > 1) {
                const vName = (plate && typeof plate === 'object') ? (plate.license_plate || plate.internal_name) : (plate || number || 'Fahrzeug');
                const vRole = (plate && typeof plate === 'object') ? (plate.internal_name || plate.license_plate) : (number || 'Fahrzeug');

                conflicts.push({
                    type: 'vehicle',
                    resourceId: vehicleId,
                    resourceName: vName,
                    role: vRole,
                    date: date,
                    reason: 'Mehrfachbelegung',
                    details: `Überschneidende Einsätze`,
                    assignments: overlappingAssignments
                });
            }
        }
    });

    // 2. Status / Maintenance Conflicts
    const vObj = (typeof plate === 'object' && plate !== null) ? plate : null;
    if (vObj && vObj.is_ready === false) {
        const displayStatus = vObj.status === 'red' ? 'Gesperrt' : (vObj.status === 'yellow' ? 'Eingeschränkt' : (vObj.status || 'Nicht bereit'));
        
        // Only report status conflicts for days the vehicle is actually assigned
        Object.keys(assignmentsByDate).forEach(date => {
            conflicts.push({
                type: 'vehicle',
                resourceId: vehicleId,
                resourceName: vObj.license_plate || vObj.internal_name || 'Fahrzeug',
                role: vObj.internal_name || 'Fahrzeug',
                date: date,
                reason: displayStatus,
                details: `Fahrzeug-Status: ${displayStatus}`,
                items: [`Status: ${displayStatus}`],
                assignments: assignmentsByDate[date],
                assignment: assignmentsByDate[date][0]
            });
        });
    }

    return conflicts;
};

/**
 * Aggregiert Konflikt-Nachrichten eines Termins auf Kolonnen-Ebene.
 * Nützlich für Zusammenfassungen (Kalender-Kacheln, Status-Badge).
 */
export const getAggregatedConflictSummary = (assignment, conflicts, workers = [], vehicles = [], kolonnen = []) => {
    const summary = {
        kolonnen: {}, // { kolonneId: Set(messages) }
        individuals: [] // List of strings: "Name: Grund"
    };

    if (!assignment || !conflicts || conflicts.total === 0) return summary;

    const assignedWorkers = assignment.workers || [];
    const assignedVehicles = assignment.vehicles || [];

    // 1. Gruppiere Ressourcen nach Kolonne
    const groups = {}; // { kolonneId: { workerIds: [], vehicleIds: [] } }
    
    assignedWorkers.forEach(w => {
        if (w.kolonne_id) {
            if (!groups[w.kolonne_id]) groups[w.kolonne_id] = { workerIds: [], vehicleIds: [] };
            groups[w.kolonne_id].workerIds.push(String(w.worker_id || w.id));
        }
    });
    assignedVehicles.forEach(v => {
        if (v.kolonne_id) {
            if (!groups[v.kolonne_id]) groups[v.kolonne_id] = { workerIds: [], vehicleIds: [] };
            groups[v.kolonne_id].vehicleIds.push(String(v.vehicle_id || v.id));
        }
    });

    // 2. Identifiziere gemeinsame Konflikte pro Kolonne & individuelle Reste
    Object.keys(groups).forEach(kId => {
        const ids = [...groups[kId].workerIds, ...groups[kId].vehicleIds];
        if (ids.length === 0) return;

        // Finde Nachrichten, die ALLE Mitglieder dieser Kolonne haben
        const firstId = ids[0];
        const sharedMsgs = new Set();
        
        const firstIdConfs = (groups[kId].workerIds.includes(firstId) ? conflicts.workers[firstId] : conflicts.vehicles[firstId]) || [];
        
        firstIdConfs.forEach(msg => {
            // Nur Doppelbelegungen können Team-Konflikte sein
            if (!msg.includes('Doppelbelegung')) return;
            
            const isSharedByAll = ids.every(id => {
                const confs = (groups[kId].workerIds.includes(id) ? conflicts.workers[id] : conflicts.vehicles[id]) || [];
                return confs.includes(msg);
            });
            
            if (isSharedByAll) sharedMsgs.add(msg);
        });

        if (sharedMsgs.size > 0) {
            summary.kolonnen[kId] = sharedMsgs;
        }
    });

    // 3. Sammle individuelle Konflikte (die nicht geteilt werden)
    assignedWorkers.forEach(aw => {
        const wId = String(aw.worker_id || aw.id);
        const confs = conflicts.workers[wId] || [];
        const kId = aw.kolonne_id;
        const shared = summary.kolonnen[kId] || new Set();

        confs.forEach(msg => {
            if (!shared.has(msg)) {
                const wData = workers.find(w => String(w.id) === wId);
                const name = wData?.name || aw.name || 'Mitarbeiter';
                summary.individuals.push(`${name}: ${msg.replace('Doppelbelegung mit Einsatz:', 'Doppelbelegung')}`);
            }
        });
    });

    assignedVehicles.forEach(av => {
        const vId = String(av.vehicle_id || av.id);
        const confs = conflicts.vehicles[vId] || [];
        const kId = av.kolonne_id;
        const shared = summary.kolonnen[kId] || new Set();

        confs.forEach(msg => {
            if (!shared.has(msg)) {
                const vData = vehicles.find(v => String(v.id) === vId);
                const name = vData?.license_plate || vData?.internal_name || 'Fahrzeug';
                summary.individuals.push(`${name}: ${msg.replace('Doppelbelegung mit Einsatz:', 'Doppelbelegung')}`);
            }
        });
    });

    return summary;
};

/**
 * Zerlegt einen Konflikt-String in ein strukturiertes Objekt für die UI.
 * Erkennt Typen wie Doppelbelegung, Abwesenheit oder Status-Fehler.
 * 
 * @param {string} str - Der rohe Konflikt-String aus dem System.
 * @returns {Object} { type, reason, subReason, detail, dateRange, original, priority }
 */
export const parseConflictString = (str) => {
    if (!str) return null;

    // 1. Doppelbelegung
    // Format: "Doppelbelegung mit Einsatz: [Projekt] ([Zeitraum])"
    if (str.includes('Doppelbelegung mit Einsatz:')) {
        const match = str.match(/Doppelbelegung mit Einsatz:\s*(.*?)\s*\((.*)\)/);
        if (match) {
            return {
                type: 'collision',
                reason: 'Mehrfachbelegung',
                detail: match[1].trim(),
                dateRange: match[2].trim(),
                priority: 2,
                original: str
            };
        }
    }

    // 2. Abwesenheit
    // Format: "Abwesend: [Grund] ([Zeitraum])"
    if (str.startsWith('Abwesend:')) {
        const match = str.match(/Abwesend:\s*(.*?)\s*\((.*)\)/);
        if (match) {
            return {
                type: 'absence',
                reason: 'Abwesenheit',
                subReason: match[1].trim(), // z.B. "Urlaub"
                detail: match[1].trim(),
                dateRange: match[2].trim(),
                priority: 1,
                original: str
            };
        }
    }

    // 3. Status (z.B. Fahrzeuge)
    // Format: "Status: [Status]"
    if (str.startsWith('Status:')) {
        const detail = str.replace('Status:', '').trim();
        return {
            type: 'status',
            reason: 'Status-Hinweis',
            subReason: detail,
            detail: detail,
            dateRange: null, // "Undatiert" entfällt hier
            priority: 0,
            original: str
        };
    }

    // 4. Fallback für generische Muster "Grund (Zeitraum)"
    const genericMatch = str.match(/(.*)\s\((.*)\)/);
    if (genericMatch) {
        return {
            type: 'generic',
            reason: genericMatch[1].trim(),
            detail: '',
            dateRange: genericMatch[2].trim(),
            priority: 3,
            original: str
        };
    }

    // 5. Absoluter Fallback
    return {
        type: 'unknown',
        reason: str,
        detail: '',
        dateRange: null,
        priority: 4,
        original: str
    };
};

/**
 * Zentralisierte Positionierungs-Logik für Hover-Cards (Konflikte & Einsätze).
 * Verhindert das Herausrutschen aus dem Viewport (Clamping) und unterstützt
 * das "Flippen" der Richtung, falls kein Platz ist.
 * 
 * @param {Object} triggerRect - getBoundingClientRect() des auslösenden Elements.
 * @param {number} cardWidth - Gewünschte Breite der Karte (z.B. 320).
 * @param {number} estimatedHeight - Geschätzte/Bevorzugte Höhe der Karte.
 * @param {Object|null} fallbackPoint - Alternative Koordinate {clientX, clientY}.
 * @param {Object} options - Zusätzliche Optionen { isSidebar, isConflict, gap }.
 * @returns {Object} { top, left, width, height, maxHeight, align }
 */
export const calculatePopupPosition = (triggerRect, cardWidth, estimatedHeight, fallbackPoint = null, options = {}) => {
    const { isSidebar = false, gap = 8, preferLeft = false } = options;
    const viewportPadding = 12;
    
    // 1. Schutzzonen berechnen (Header, Filter, Checkboxen)
    const headerEl = document.querySelector('.app-header');
    const listHeaderEl = document.querySelector('.leader-list-header');
    const listFilterEl = document.querySelector('.leader-list-filter-row');
    const selectionCol = document.querySelector('.checkbox-col');
    const conflictCol = document.querySelector('.conflict-col');
    
    const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
    const listHeaderBottom = listHeaderEl ? listHeaderEl.getBoundingClientRect().bottom : 0;
    const listFilterBottom = listFilterEl ? listFilterEl.getBoundingClientRect().bottom : 0;
    
    // safeRight of selection/icons: Take the rightmost of both columns
    const selectionRight = selectionCol ? selectionCol.getBoundingClientRect().right : 0;
    const conflictRight = conflictCol ? conflictCol.getBoundingClientRect().right : 0;
    const totalSelectionRight = Math.max(selectionRight, conflictRight);
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // safeTop: Muss unter dem höchsten fixierten Element liegen (App-Header oder Tabellen-Header)
    const safeTop = Math.max(viewportPadding, Math.ceil(Math.max(headerBottom, listHeaderBottom, listFilterBottom)) + 4);
    const safeBottom = viewportHeight - viewportPadding;
    // safeLeft: Muss rechts von der Auswahl-Spalte liegen (falls vorhanden)
    const safeLeft = Math.max(viewportPadding, Math.ceil(totalSelectionRight) + 4);
    const safeRight = viewportWidth - viewportPadding; 

    // 2. Ankerpunkt bestimmen
    const useMouseAnchor = !!fallbackPoint && !isSidebar && !options.useTriggerOnly;
    
    // anchorX defines the starting horizontal coordinate
    let anchorX;
    if (useMouseAnchor) {
        anchorX = fallbackPoint.clientX;
    } else if (triggerRect) {
        if (isSidebar) {
            // Sidebars: use the inner edge
            anchorX = preferLeft ? triggerRect.left : triggerRect.right;
        } else {
            // Calendar entries: use the left or right edge depending on preference
            anchorX = preferLeft ? triggerRect.left : triggerRect.left; // anchorX is usually start of trigger
        }
    } else {
        anchorX = safeLeft;
    }

    const anchorY = useMouseAnchor ? fallbackPoint.clientY : (triggerRect ? triggerRect.top + triggerRect.height / 2 : safeTop);

    // 3. Horizontale Positionierung
    let left;
    if (isSidebar) {
        if (preferLeft) {
            left = anchorX - gap - cardWidth;
        } else {
            left = anchorX + gap;
        }
    } else if (useMouseAnchor) {
        // Bei Maus-Anker: Versuche rechts von der Maus, dann links (oder umgekehrt bei preferLeft)
        const canFitRight = anchorX + gap + cardWidth <= safeRight;
        const canFitLeft = anchorX - gap - cardWidth >= safeLeft;
        
        if (preferLeft) {
            if (canFitLeft) left = anchorX - gap - cardWidth;
            else if (canFitRight) left = anchorX + gap;
            else left = anchorX - cardWidth / 2;
        } else {
            if (canFitRight) left = anchorX + gap;
            else if (canFitLeft) left = anchorX - gap - cardWidth;
            else left = anchorX - cardWidth / 2;
        }
    } else {
        // Standard (z.B. Kalender-Kachel): Rechts vom Trigger bevorzugen, dann Links
        const triggerWidth = triggerRect?.width || 0;
        const canFitRight = anchorX + triggerWidth + gap + cardWidth <= safeRight;
        const canFitLeft = anchorX - gap - cardWidth >= safeLeft;
        
        if (preferLeft) {
            if (canFitLeft) left = anchorX - gap - cardWidth;
            else if (canFitRight) left = anchorX + triggerWidth + gap;
            else left = anchorX + triggerWidth / 2 - cardWidth / 2;
        } else {
            if (canFitRight) left = anchorX + triggerWidth + gap;
            else if (canFitLeft) left = anchorX - gap - cardWidth;
            else left = anchorX + triggerWidth / 2 - cardWidth / 2;
        }
    }

    // Finales Clamping an die Schutzzonen
    left = Math.max(safeLeft, Math.min(left, safeRight - cardWidth));

    // 4. Vertikale Positionierung (Zentrierung zum Anker mit Clamping)
    let top = anchorY - estimatedHeight / 2;
    
    if (top + estimatedHeight > safeBottom) {
        top = safeBottom - estimatedHeight;
    }
    if (top < safeTop) {
        top = safeTop;
    }

    // Verfügbare Höhe für Scrolling-Logik berechnen
    const availableHeight = safeBottom - top;
    const finalMaxHeight = Math.max(200, availableHeight);

    return { 
        top, 
        left, 
        width: cardWidth, 
        height: Math.min(estimatedHeight, availableHeight), 
        maxHeight: finalMaxHeight, 
        align: 'top' 
    };
};

/**
 * Calculates a logical conflict count by grouping shared conflicts.
 * This matches the visual count in the Conflict Center.
 */
export const getLogicalConflictCount = (conflicts, kolonnen = []) => {
    if (!conflicts || conflicts.length === 0) return 0;

    const resToKMap = {};
    kolonnen.forEach(k => {
        (k.members || []).forEach(m => {
            const key = `${m.type}-${m.resource_id}`;
            if (!resToKMap[key]) resToKMap[key] = [];
            resToKMap[key].push(String(k.id));
        });
    });

    const sharedKeyMap = {};
    const handledIds = new Set();
    const todayStr = new Date().toISOString().split('T')[0];
    const currentConflicts = conflicts.filter(c => c.date >= todayStr);

    currentConflicts.forEach(c => {
        const involvedKIds = new Set();
        const involvedAssignments = (c.assignments || [c.assignment]).filter(Boolean);
        
        involvedAssignments.forEach(a => {
            const list = c.type === 'worker' ? a.workers : a.vehicles;
            const res = (list || []).find(r => String(r.worker_id || r.id) === String(c.resourceId));
            if (res?.kolonne_id) involvedKIds.add(String(res.kolonne_id));
        });

        if (involvedKIds.size === 0) {
            const globalKIds = resToKMap[`${c.type}-${c.resourceId}`] || [];
            globalKIds.forEach(id => involvedKIds.add(id));
        }

        if (!c.reason.toLowerCase().includes('abwesenheit') && !c.absence && involvedAssignments.length > 0) {
            const aIds = involvedAssignments.map(a => String(a.id)).sort().join(',');
            involvedKIds.forEach(kId => {
                const key = `${kId}-${c.date}-${c.reason}-${aIds}`;
                if (!sharedKeyMap[key]) {
                    sharedKeyMap[key] = { memberMatches: new Set(), sourceConflicts: [] };
                }
                sharedKeyMap[key].memberMatches.add(`${c.type}-${c.resourceId}`);
                sharedKeyMap[key].sourceConflicts.push(c);
            });
        }
    });

    let logicalCount = 0;
    // 1. Count shared groups
    Object.values(sharedKeyMap).forEach(group => {
        if (group.memberMatches.size > 1) {
            logicalCount++;
            group.sourceConflicts.forEach(c => {
                handledIds.add(`${c.type}-${c.resourceId}-${c.date}-${c.reason}`);
            });
        }
    });

    // 2. Count remaining individual conflicts
    currentConflicts.forEach(c => {
        if (!handledIds.has(`${c.type}-${c.resourceId}-${c.date}-${c.reason}`)) {
            logicalCount++;
        }
    });

    return logicalCount;
};
/**
 * Sortiert Einsätze nach Startzeit, dann Stack-Order, dann ID.
 * Gewährleistet eine konsistente visuelle Darstellung über alle Ansichten hinweg.
 */
export const sortAssignmentsByVisualOrder = (list) => {
    return [...(list || [])].sort((a, b) => {
        const aStart = a?.start_datetime ? new Date(a.start_datetime).getTime() : Number.POSITIVE_INFINITY;
        const bStart = b?.start_datetime ? new Date(b.start_datetime).getTime() : Number.POSITIVE_INFINITY;
        if (aStart !== bStart) return aStart - bStart;

        const aOrder = Number.isFinite(Number(a?.stack_order)) ? Number(a.stack_order) : 0;
        const bOrder = Number.isFinite(Number(b?.stack_order)) ? Number(b.stack_order) : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;

        return String(a?.id || '').localeCompare(String(b?.id || ''));
    });
};
